import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  ArrowLeft,
  CheckCircle,
  Columns,
  Eye,
  GripVertical,
  Image as ImageIcon,
  LayoutGrid,
  Minus,
  Pencil,
  PlusCircle,
  Save,
  Scissors,
  Trash2,
  Type,
} from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'
import {
  Align,
  ComponentType,
  ReceiptBlock,
  ReceiptConfig,
  SectionKey,
  TextFont,
  TextSize,
} from '../../all-types'
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks'
import { updateClientInfo } from '../../services'

const PALETTE_ID = 'palette'
const DEFAULT_TEMPLATE_NAME = 'Default Receipt'
type DesignerView = 'templates' | 'editor'
const SECTION_KEYS: SectionKey[] = [
  'receiptHeader',
  'headerExtras',
  'itemsBody',
  'footerExtras',
  'receiptFooter',
]

const createBlockId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `blk_${crypto.randomUUID()}`
  }

  return `blk_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

const createTemplateId = () => createBlockId().replace('blk_', 'tpl_')

const createEmptyReceiptConfig = (
  name = DEFAULT_TEMPLATE_NAME
): ReceiptConfig => ({
  id: createTemplateId(),
  name,
  receiptHeader: [],
  headerExtras: [],
  itemsBody: [],
  footerExtras: [],
  receiptFooter: [],
})

const normalizeReceiptConfig = (
  receiptProperties?: Partial<ReceiptConfig>
): ReceiptConfig => {
  const usedIds = new Set<string>()
  const normalizedConfig = createEmptyReceiptConfig(
    receiptProperties?.name || DEFAULT_TEMPLATE_NAME
  )
  normalizedConfig.id = receiptProperties?.id || createTemplateId()

  SECTION_KEYS.forEach((sectionKey) => {
    const blocks = receiptProperties?.[sectionKey] || []

    normalizedConfig[sectionKey] = blocks.map((block) => {
      const isExistingIdUsable = block.id && !usedIds.has(block.id)
      const id = isExistingIdUsable ? block.id : createBlockId()

      usedIds.add(id)

      return { ...block, id }
    })
  })

  return normalizedConfig
}

const getTemplateName = (template: ReceiptConfig, index: number) =>
  template.name?.trim() || `Receipt Template ${index + 1}`

const getSavedReceiptTemplates = (infoReceiptData?: {
  savedReceipts?: ReceiptConfig[]
  receiptProperties?: ReceiptConfig
}) => {
  const activeReceipt = normalizeReceiptConfig(
    infoReceiptData?.receiptProperties
  )
  const savedReceipts = infoReceiptData?.savedReceipts?.length
    ? infoReceiptData.savedReceipts
    : [activeReceipt]

  return savedReceipts.map((template, index) =>
    normalizeReceiptConfig({
      ...template,
      name: getTemplateName(template, index),
    })
  )
}

const getActiveTemplateIndex = (
  templates: ReceiptConfig[],
  activeReceipt?: ReceiptConfig
) => {
  const activeId = activeReceipt?.id
  const activeName = activeReceipt?.name?.trim()

  if (activeId) {
    const activeIndex = templates.findIndex(
      (template) => template.id === activeId
    )

    if (activeIndex >= 0) return activeIndex
  }

  if (!activeName) return 0

  const activeIndex = templates.findIndex(
    (template) => template.name?.trim() === activeName
  )

  return activeIndex >= 0 ? activeIndex : 0
}

const BlockEditor = ({
  block,
  section,
  index,
  onUpdate,
  onRemove,
}: {
  block: ReceiptBlock
  section: SectionKey
  index: number
  onUpdate: (s: SectionKey, id: string, u: Partial<ReceiptBlock>) => void
  onRemove: (s: SectionKey, id: string) => void
}) => (
  <Draggable draggableId={block.id} index={index}>
    {(provided, snapshot) => (
      <Paper
        ref={provided.innerRef}
        {...provided.draggableProps}
        elevation={snapshot.isDragging ? 4 : 0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderRadius: 2,
          borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          bgcolor: 'background.paper',
          '&:hover': {
            borderColor: 'primary.light',
            boxShadow: (theme) => theme.shadows[2],
          },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Box
            {...provided.dragHandleProps}
            sx={{
              display: 'flex',
              color: 'text.disabled',
              cursor: 'grab',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <GripVertical size={18} />
          </Box>
          <Typography
            variant="caption"
            sx={{
              bgcolor: 'action.hover',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontSize: '10px',
            }}
          >
            {block.type}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={() => onRemove(section, block.id)}
            sx={{
              color: 'text.disabled',
              '&:hover': { color: 'error.main', bgcolor: 'error.lighter' },
            }}
          >
            <Trash2 size={14} />
          </IconButton>
        </Stack>

        <Grid container spacing={2}>
          {(block.type === 'text' || block.type === 'image') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={block.type === 'text' ? 'Content' : 'Image URL'}
                value={block.content || ''}
                onChange={(e) =>
                  onUpdate(section, block.id, { content: e.target.value })
                }
              />
            </Grid>
          )}

          {block.type === 'row' && (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Left Label"
                  value={block.left || ''}
                  onChange={(e) =>
                    onUpdate(section, block.id, { left: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Right Value"
                  value={block.right || ''}
                  onChange={(e) =>
                    onUpdate(section, block.id, { right: e.target.value })
                  }
                />
              </Grid>
            </>
          )}

          {block.type === 'image' && (
            <>
              <Grid item xs={4}>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  label="Width"
                  value={block.width || 100}
                  onChange={(e) =>
                    onUpdate(section, block.id, {
                      width: parseInt(e.target.value) || 100,
                    })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  label="Height"
                  value={block.height || 100}
                  onChange={(e) =>
                    onUpdate(section, block.id, {
                      height: parseInt(e.target.value) || 100,
                    })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Alignment</InputLabel>
                  <Select
                    value={block.align || 'center'}
                    onChange={(e) =>
                      onUpdate(section, block.id, {
                        align: e.target.value as Align,
                      })
                    }
                    label="Alignment"
                  >
                    <MenuItem value="left">Left</MenuItem>
                    <MenuItem value="center">Center</MenuItem>
                    <MenuItem value="right">Right</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {(block.type === 'text' || block.type === 'row') && (
            <>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Font</InputLabel>
                  <Select
                    value={block.font || 'A'}
                    onChange={(e) =>
                      onUpdate(section, block.id, {
                        font: e.target.value as TextFont,
                      })
                    }
                    label="Font"
                  >
                    {['A', 'B', 'C', 'D', 'E', 'special-A', 'special-B'].map(
                      (f) => (
                        <MenuItem key={f} value={f}>
                          Font {f}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  label="Width (1-8)"
                  value={block.sizeW || 1}
                  onChange={(e) =>
                    onUpdate(section, block.id, {
                      sizeW: (parseInt(e.target.value) || 1) as TextSize,
                    })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  label="Height (1-8)"
                  value={block.sizeH || 1}
                  onChange={(e) =>
                    onUpdate(section, block.id, {
                      sizeH: (parseInt(e.target.value) || 1) as TextSize,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Alignment</InputLabel>
                    <Select
                      value={block.align || 'center'}
                      onChange={(e) =>
                        onUpdate(section, block.id, {
                          align: e.target.value as Align,
                        })
                      }
                      label="Alignment"
                    >
                      <MenuItem value="left">Left</MenuItem>
                      <MenuItem value="center">Center</MenuItem>
                      <MenuItem value="right">Right</MenuItem>
                    </Select>
                  </FormControl>
                  <Stack direction="row" alignItems="center">
                    <Checkbox
                      size="small"
                      checked={!!block.bold}
                      onChange={(e) =>
                        onUpdate(section, block.id, { bold: e.target.checked })
                      }
                    />
                    <Typography variant="body2" fontWeight={500}>
                      Bold Text
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    )}
  </Draggable>
)

const PaletteBlock = ({
  type,
  label,
  icon,
  index,
}: {
  type: ComponentType
  label: string
  icon: React.ReactNode
  index: number
}) => (
  <Draggable draggableId={`palette_${type}`} index={index}>
    {(provided, snapshot) => (
      <ListItem
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        disablePadding
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: snapshot.isDragging ? 'primary.main' : 'transparent',
          bgcolor: snapshot.isDragging ? 'primary.lighter' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ListItemButton sx={{ py: 1 }}>
          <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </ListItemButton>
      </ListItem>
    )}
  </Draggable>
)

export const ReceiptDesigner: React.FC = () => {
  const info = useAppSelector((state) => state.clientInfo.data)
  const initialTemplates = getSavedReceiptTemplates(info)
  const initialTemplateIndex = getActiveTemplateIndex(
    initialTemplates,
    info?.receiptProperties
  )
  const [savedReceipts, setSavedReceipts] =
    useState<ReceiptConfig[]>(initialTemplates)
  const [selectedTemplateIndex, setSelectedTemplateIndex] =
    useState(initialTemplateIndex)
  const [config, setConfig] = useState<ReceiptConfig>(
    () =>
      initialTemplates[initialTemplateIndex] ||
      normalizeReceiptConfig(info?.receiptProperties)
  )
  const [templateName, setTemplateName] = useState(
    getTemplateName(
      initialTemplates[initialTemplateIndex] ||
        normalizeReceiptConfig(info?.receiptProperties),
      initialTemplateIndex
    )
  )
  const [designerView, setDesignerView] = useState<DesignerView>('templates')
  const [isSaving, setIsSaving] = useState(false)
  const dispatcher = useAppDispatch()
  const selectedTemplate = savedReceipts[selectedTemplateIndex]

  useEffect(() => {
    const nextTemplates = getSavedReceiptTemplates(info)
    const nextSelectedIndex = getActiveTemplateIndex(
      nextTemplates,
      info?.receiptProperties
    )

    setSavedReceipts(nextTemplates)
    setSelectedTemplateIndex(nextSelectedIndex)
    setConfig(
      nextTemplates[nextSelectedIndex] ||
        normalizeReceiptConfig(info?.receiptProperties)
    )
    setTemplateName(
      getTemplateName(
        nextTemplates[nextSelectedIndex] ||
          normalizeReceiptConfig(info?.receiptProperties),
        nextSelectedIndex
      )
    )
  }, [info])

  const persistReceiptData = async (
    nextReceiptProperties: ReceiptConfig,
    nextSavedReceipts: ReceiptConfig[]
  ) => {
    const tempClient = {
      ...info,
      savedReceipts: nextSavedReceipts,
      receiptProperties: nextReceiptProperties,
    }

    await dispatcher(
      updateClientInfo({ clientId: tempClient._id || '', body: tempClient })
    )
  }

  const updateClientdata = async () => {
    setIsSaving(true)
    try {
      const nextReceiptProperties = normalizeReceiptConfig({
        ...config,
        id: config.id || selectedTemplate?.id || createTemplateId(),
        name:
          templateName.trim() || getTemplateName(config, selectedTemplateIndex),
      })
      const nextSavedReceipts = savedReceipts.length
        ? [...savedReceipts]
        : [nextReceiptProperties]
      const nextSelectedIndex =
        selectedTemplateIndex >= 0 &&
        selectedTemplateIndex < nextSavedReceipts.length
          ? selectedTemplateIndex
          : nextSavedReceipts.length

      nextSavedReceipts[nextSelectedIndex] = nextReceiptProperties

      setConfig(nextReceiptProperties)
      setSavedReceipts(nextSavedReceipts)
      setSelectedTemplateIndex(nextSelectedIndex)

      await persistReceiptData(nextReceiptProperties, nextSavedReceipts)
    } catch (e) {
      console.error(e)
    } finally {
      setIsSaving(false)
    }
  }

  const selectTemplate = (index: number) => {
    const selectedTemplate = savedReceipts[index]

    if (!selectedTemplate) return

    setSelectedTemplateIndex(index)
    setConfig(normalizeReceiptConfig(selectedTemplate))
    setTemplateName(getTemplateName(selectedTemplate, index))
  }

  const openTemplateDesigner = (index: number) => {
    selectTemplate(index)
    setDesignerView('editor')
  }

  const useTemplate = async (index: number) => {
    const template = savedReceipts[index]

    if (!template) return

    const nextReceiptProperties = normalizeReceiptConfig(template)

    setIsSaving(true)
    try {
      setSelectedTemplateIndex(index)
      setConfig(nextReceiptProperties)
      setTemplateName(getTemplateName(nextReceiptProperties, index))

      await persistReceiptData(nextReceiptProperties, savedReceipts)
    } catch (e) {
      console.error(e)
    } finally {
      setIsSaving(false)
    }
  }

  const createNewTemplate = () => {
    const nextTemplate = createEmptyReceiptConfig(
      `Receipt Template ${savedReceipts.length + 1}`
    )
    const nextSavedReceipts = [...savedReceipts, nextTemplate]

    setSavedReceipts(nextSavedReceipts)
    setSelectedTemplateIndex(nextSavedReceipts.length - 1)
    setConfig(nextTemplate)
    setTemplateName(getTemplateName(nextTemplate, nextSavedReceipts.length - 1))
    setDesignerView('editor')
  }

  const deleteTemplate = async (index: number) => {
    if (!savedReceipts[index]) return

    const nextSavedReceipts = savedReceipts.filter((_, idx) => idx !== index)
    const nextSelectedIndex = nextSavedReceipts.length
      ? index === selectedTemplateIndex
        ? Math.min(index, nextSavedReceipts.length - 1)
        : index < selectedTemplateIndex
          ? selectedTemplateIndex - 1
          : selectedTemplateIndex
      : 0
    const nextReceiptProperties = normalizeReceiptConfig(
      nextSavedReceipts[nextSelectedIndex] ||
        createEmptyReceiptConfig(DEFAULT_TEMPLATE_NAME)
    )
    const templatesToSave = nextSavedReceipts.length
      ? nextSavedReceipts
      : [nextReceiptProperties]

    setIsSaving(true)
    try {
      setSavedReceipts(templatesToSave)
      setSelectedTemplateIndex(nextSelectedIndex)
      setConfig(nextReceiptProperties)
      setTemplateName(getTemplateName(nextReceiptProperties, nextSelectedIndex))

      await persistReceiptData(nextReceiptProperties, templatesToSave)
    } catch (e) {
      console.error(e)
    } finally {
      setIsSaving(false)
    }
  }

  const updateConfig = useCallback(
    (updater: (prev: ReceiptConfig) => ReceiptConfig) => {
      setConfig((prev) => {
        const nextConfig = updater(prev)

        setSavedReceipts((prevSavedReceipts) =>
          prevSavedReceipts.map((template, index) =>
            index === selectedTemplateIndex ? nextConfig : template
          )
        )

        return nextConfig
      })
    },
    [selectedTemplateIndex]
  )

  const updateTemplateName = useCallback(
    (name: string) => {
      setTemplateName(name)
      updateConfig((prev) => ({
        ...prev,
        name,
      }))
    },
    [updateConfig]
  )

  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result
    if (!destination) return

    const sourceDroppableId = source.droppableId
    const destDroppableId = destination.droppableId

    if (sourceDroppableId === PALETTE_ID) {
      const componentType = draggableId.split('_')[1] as ComponentType
      const newBlock: ReceiptBlock = {
        id: createBlockId(),
        type: componentType,
        content: '',
        left: '',
        right: '',
        align: 'center',
        bold: false,
        width: 100,
        height: 100,
        sizeW: 1,
        sizeH: 1,
        font: 'A',
      }
      const destKey = destDroppableId as SectionKey
      updateConfig((prev) => {
        const destItems = Array.from(prev[destKey])
        destItems.splice(destination.index, 0, newBlock)

        return { ...prev, [destKey]: destItems }
      })
      return
    }

    const sourceKey = sourceDroppableId as SectionKey
    const destKey = destDroppableId as SectionKey
    updateConfig((prev) => {
      const sourceItems = Array.from(prev[sourceKey])
      const [removed] = sourceItems.splice(source.index, 1)

      if (!removed) return prev

      if (sourceKey === destKey) {
        sourceItems.splice(destination.index, 0, removed)
        return { ...prev, [sourceKey]: sourceItems }
      }

      const destItems = Array.from(prev[destKey])
      destItems.splice(destination.index, 0, removed)

      return {
        ...prev,
        [sourceKey]: sourceItems,
        [destKey]: destItems,
      }
    })
  }

  const updateBlock = useCallback(
    (s: SectionKey, id: string, u: Partial<ReceiptBlock>) => {
      updateConfig((prev) => ({
        ...prev,
        [s]: prev[s].map((b) => (b.id === id ? { ...b, ...u } : b)),
      }))
    },
    [updateConfig]
  )

  const removeBlock = useCallback(
    (s: SectionKey, id: string) => {
      updateConfig((prev) => ({
        ...prev,
        [s]: prev[s].filter((b) => b.id !== id),
      }))
    },
    [updateConfig]
  )

  if (designerView === 'templates') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 4,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Receipt Templates
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a template to print with, or open one to edit its layout
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PlusCircle size={18} />}
            onClick={createNewTemplate}
            disabled={isSaving}
            sx={{ borderRadius: 2 }}
          >
            Add Template
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {savedReceipts.map((template, index) => {
            const isSelected = selectedTemplateIndex === index

            return (
              <Grid item xs={12} md={6} lg={4} key={template.id || index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected
                      ? 'primary.lighter'
                      : 'background.paper',
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={800} noWrap>
                          {getTemplateName(template, index)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {SECTION_KEYS.reduce(
                            (total, sectionKey) =>
                              total + template[sectionKey].length,
                            0
                          )}{' '}
                          blocks
                        </Typography>
                      </Box>
                      {isSelected && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ color: 'primary.main' }}
                        >
                          <CheckCircle size={16} />
                          <Typography variant="caption" fontWeight={800}>
                            Selected
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      <Button
                        fullWidth
                        variant={isSelected ? 'contained' : 'outlined'}
                        startIcon={<CheckCircle size={16} />}
                        onClick={() => useTemplate(index)}
                        disabled={isSaving}
                        sx={{ borderRadius: 2 }}
                      >
                        Use
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Pencil size={16} />}
                        onClick={() => openTemplateDesigner(index)}
                        disabled={isSaving}
                        sx={{ borderRadius: 2 }}
                      >
                        Design
                      </Button>
                      <IconButton
                        size="small"
                        disabled={isSaving}
                        onClick={() => deleteTemplate(index)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'error.main',
                            borderColor: 'error.main',
                            bgcolor: 'error.lighter',
                          },
                        }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            )
          })}
        </Grid>
      </Box>
    )
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        {/* Components Sidebar - Fixed */}
        <Box
          sx={{
            width: 280,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <LayoutGrid size={20} />
              <Typography variant="subtitle1" fontWeight={700}>
                Components
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
            <Droppable droppableId={PALETTE_ID} isDropDisabled={true}>
              {(provided) => (
                <List {...provided.droppableProps} ref={provided.innerRef}>
                  {[
                    {
                      type: 'text',
                      icon: <Type size={18} />,
                      label: 'Text Line',
                    },
                    {
                      type: 'row',
                      icon: <Columns size={18} />,
                      label: 'Dual Column Row',
                    },
                    {
                      type: 'image',
                      icon: <ImageIcon size={18} />,
                      label: 'Image/Logo',
                    },
                    {
                      type: 'line',
                      icon: <Minus size={18} />,
                      label: 'Divider Line',
                    },
                    {
                      type: 'br',
                      icon: <PlusCircle size={18} />,
                      label: 'Spacer',
                    },
                    {
                      type: 'cut',
                      icon: <Scissors size={18} />,
                      label: 'Paper Cut',
                    },
                  ].map((item, index) => (
                    <PaletteBlock
                      key={item.type}
                      type={item.type as ComponentType}
                      label={item.label}
                      icon={item.icon}
                      index={index}
                    />
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </Box>
        </Box>

        {/* Center - Scrollable Builder */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ArrowLeft size={18} />}
                onClick={() => setDesignerView('templates')}
                sx={{ borderRadius: 2 }}
              >
                Templates
              </Button>
              <Box>
                <Typography variant="h5" fontWeight={800}>
                  Receipt Designer
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customize your thermal printer layout
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                size="small"
                label="Template Name"
                value={templateName}
                onChange={(e) => updateTemplateName(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <Button
                variant="contained"
                startIcon={<Save size={18} />}
                onClick={updateClientdata}
                disabled={isSaving}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {isSaving ? 'Saving...' : 'Save Template'}
              </Button>
            </Stack>
          </Stack>

          {SECTION_KEYS.map((sKey) => (
            <Box key={sKey}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  color: 'primary.main',
                  mb: 1,
                  display: 'block',
                  letterSpacing: 1.2,
                }}
              >
                {sKey.replace(/([A-Z])/g, ' $1')}
              </Typography>
              <Droppable droppableId={sKey}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      minHeight: 80,
                      p: 2,
                      borderRadius: 3,
                      border: '2px dashed',
                      borderColor: snapshot.isDraggingOver
                        ? 'primary.main'
                        : 'divider',
                      bgcolor: snapshot.isDraggingOver
                        ? 'action.hover'
                        : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {config[sKey].map((block, idx) => (
                      <BlockEditor
                        key={block.id}
                        block={block}
                        section={sKey}
                        index={idx}
                        onUpdate={updateBlock}
                        onRemove={removeBlock}
                      />
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Box>
          ))}
        </Box>

        {/* Right Sidebar - Fixed Preview */}
        <Box
          sx={{
            width: 400,
            borderLeft: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Eye size={20} />
              <Typography variant="subtitle1" fontWeight={700}>
                Live Preview
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              //p: 4,
              overflowY: 'auto',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Paper
              elevation={6}
              sx={{
                width: '100%',
                //maxWidth: 300,
                height: 'fit-content',
                minHeight: 500,
                bgcolor: '#fff',
                p: 3,
                borderRadius: 1,
                fontFamily: '"Courier New", Courier, monospace',
                boxShadow: '0px 10px 30px rgba(0,0,0,0.05)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 10,
                  background:
                    'linear-gradient(45deg, transparent 33.333%, #fff 33.333%, #fff 66.666%, transparent 66.666%), linear-gradient(-45deg, transparent 33.333%, #fff 33.333%, #fff 66.666%, transparent 66.666%)',
                  backgroundSize: '10px 20px',
                  transform: 'translateY(100%)',
                },
              }}
            >
              {SECTION_KEYS.map((key) => (
                <Box
                  key={key}
                  sx={{ mb: 2, pb: 1, borderBottom: '1px dashed #eee' }}
                >
                  {config[key].map((b) => (
                    <Box
                      key={b.id}
                      sx={{
                        mb: 0.5,
                        textAlign: b.align,
                        fontWeight: b.bold ? 'bold' : 'normal',
                        fontSize: b.font === 'B' ? '11px' : '13px',
                        transform:
                          b.type === 'text' || b.type === 'row'
                            ? `scale(${b.sizeW || 1}, ${b.sizeH || 1})`
                            : 'none',
                        transformOrigin:
                          b.align === 'center'
                            ? 'center'
                            : b.align === 'right'
                              ? 'right'
                              : 'left',
                        color: '#000',
                      }}
                    >
                      {b.type === 'text' && (
                        <Box sx={{ wordBreak: 'break-all' }}>
                          {b.content || '••••••••'}
                        </Box>
                      )}
                      {b.type === 'row' && (
                        <Stack direction="row" justifyContent="space-between">
                          <span>{b.left || 'Label'}</span>
                          <span>{b.right || 'Value'}</span>
                        </Stack>
                      )}
                      {b.type === 'image' && b.content && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent:
                              b.align === 'left'
                                ? 'flex-start'
                                : b.align === 'right'
                                  ? 'flex-end'
                                  : 'center',
                            my: 1,
                          }}
                        >
                          <img
                            src={b.content}
                            style={{
                              width: b.width,
                              height: b.height,
                              filter: 'grayscale(1)',
                            }}
                          />
                        </Box>
                      )}
                      {b.type === 'line' && (
                        <Divider
                          sx={{
                            my: 1,
                            borderColor: '#000',
                            borderStyle: 'dashed',
                          }}
                        />
                      )}
                      {b.type === 'br' && <Box sx={{ height: 8 }} />}
                      {b.type === 'cut' && (
                        <Box
                          sx={{
                            borderTop: '1px dotted #ccc',
                            my: 2,
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <Scissors
                            size={12}
                            style={{ marginTop: -7, color: '#ccc' }}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Paper>
          </Box>
        </Box>
      </Box>
    </DragDropContext>
  )
}
